import { resolveRhcFacilityTypeOverride } from "./rhcFacilityType";
export const SELECTED_FACILITY_STORAGE_KEY = "filtered_selectedFacility";

/** Dispatched on `window` after sessionStorage updates the selected facility (same-tab listeners). */
export const SELECTED_FACILITY_UPDATED_EVENT = "unicomply:selected-facility-updated";

export function notifySelectedFacilitySessionUpdated(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(SELECTED_FACILITY_UPDATED_EVENT));
}

/**
 * Remove session keys that tie the UI to a facility or pricing flow for another country.
 * Call whenever the sidebar country changes so India (etc.) does not reuse a USA selection.
 */
export function clearFacilitySessionSelectionForCountryChange(): void {
  if (typeof window === "undefined") return;
  const keys = [
    SELECTED_FACILITY_STORAGE_KEY,
    "preferred_facility_for_pricing",
    "current_verification_facility",
    "selected_plan",
    "sessionNpiNumber",
    "sessionPhoneNumber",
    "sessionFacilityName",
    "sessionFacilityType",
    "sessionState",
    "sessionCity",
    "filtered_facilityTypes",
    "filtered_category",
    "DisplayPage",
    "facilitySearchInProgress",
  ];
  for (const k of keys) {
    try {
      sessionStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  }
}

export function getSelectedFacilityFromSession<T = any>(): T | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = sessionStorage.getItem(SELECTED_FACILITY_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as T) : null;
  } catch (error) {
    console.error("Error parsing selected facility from sessionStorage:", error);
    return null;
  }
}

/** Fields used to build facility picker labels (My Facility dropdown, etc.). */
export type FacilityDropdownLabelInput = {
  facility_name?: string | null;
  city?: string | null;
  state?: string | null;
  address_1?: string | null;
  address_2?: string | null;
  full_address?: string | null;
  /** CCN or NPI (often 10 digits; may be prefixed e.g. `user_1528171857`). */
  ccn_number?: string | null;
  /** When stored separately from {@link ccn_number} (same resolution rules for display). */
  npi_number?: string | null;
  /** Raw unique id when the API sends it besides {@link ccn_number}. */
  facility_unique_id?: string | null;
  /** Optional CMS CCN saved apart from the NPI unique id. */
  medicare_ccn?: string | null;
  /** Secondary registry ids saved at signup (healthcare rows may store NPI here when unique id is CCN). */
  profile_targeting_json?: Record<string, unknown> | null;
  /** ISO-style code from facility (e.g. USA, CAN, IND, UAE) — drives registry label, not US NPI. */
  country_code?: string | null;
};

export type FacilityHierarchyInput = FacilityDropdownLabelInput & {
  id?: number | null;
  facility_id?: number | null;
  /** Some API rows use organization_name / org_name instead of facility_name. */
  org_name?: string | null;
  organization_name?: string | null;
  facility_unique_id?: string | null;
  facility_group_id?: string | null;
  parent_facility_group_id?: string | null;
  is_parent_facility?: boolean | null;
  is_child_facility?: boolean | null;
  child_facilities?: FacilityHierarchyInput[] | null;
  is_network?: boolean | null;
  facility_type?: string | null;
  facility_type_id?: number | null;
  facility_type_slug?: string | null;
  plan_status?: string | null;
  is_restricted?: boolean | null;
};

export type GroupedFacilityOption<T extends FacilityHierarchyInput> = {
  facility: T;
  depth: number;
  isParent: boolean;
  key: string;
};

/** Last 10 digits when string ends with an NPI-style identifier, else 10-digit-only whole string. */
export function bareNpiFromCcn(ccn: string | null | undefined): string {
  if (ccn == null || typeof ccn !== "string") return "";
  const t = ccn.trim();
  if (!t) return "";
  const m = t.match(/(\d{10})$/);
  if (m) return m[1];
  if (/^\d{10}$/.test(t)) return t;
  return "";
}

/** NUCC-style provider taxonomy: 10 chars in CMS-published code shapes only. */
const _NUCC_TAXONOMY_CODE = /^(\d{9}[A-Z]|\d{4}[A-Z]\d{4}[A-Z]|\d{3}[A-Z]{2}\d{4}[A-Z]|\d{3}[A-Z]\d{5}[A-Z])$/i;

function _isPlausibleNuccTaxonomyCode(code: string): boolean {
  const t = code.trim();
  return t.length === 10 && _NUCC_TAXONOMY_CODE.test(t);
}

/**
 * True when a 10-digit string satisfies the CMS NPI Luhn check (80840 + first 9 digits).
 * Does not confirm NPPES enumeration — only the check digit.
 */
export function isValidUsNpiChecksum(npi10: string): boolean {
  if (!/^\d{10}$/.test(npi10)) return false;
  const d = `80840${npi10.slice(0, 9)}`;
  let sum = 0;
  let alt = true;
  for (let i = d.length - 1; i >= 0; i--) {
    let n = +d[i];
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  const check = (10 - (sum % 10)) % 10;
  return check === +npi10[9];
}

/** Bare 10 digits from {@link bareNpiFromCcn} only when the US NPI check digit is valid. */
export function verifiedUsNpiDigitsFromIdField(
  idRaw: string | null | undefined,
): string {
  const bare = bareNpiFromCcn(idRaw);
  return bare && isValidUsNpiChecksum(bare) ? bare : "";
}

/**
 * Conservative CMS CCN shape for UI labels: 10 alphanumerics with a letter,
 * not an internal prefix, and not a valid US NPI.
 */
export function isPlausibleMedicareCcn(raw: string | null | undefined): boolean {
  const t = typeof raw === "string" ? raw.trim().toUpperCase() : "";
  if (t.length !== 10 || !/^[A-Z0-9]{10}$/.test(t)) return false;
  if (/^FAC/.test(t) || /^USER/.test(t)) return false;
  if (!/[A-Z]/.test(t)) return false;
  const digits = t.replace(/\D/g, "");
  if (digits.length === 10 && isValidUsNpiChecksum(digits)) return false;
  return true;
}

function _trimStr(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

function _normSidebarDedupe(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

/** Index every registry id. `ccn_number ?? npi_number` dropped NPI when CCN 500124 was set. */
function _addSidebarRegistryKeys(keys: Set<string>, raw: unknown): void {
  const idStr = raw != null ? String(raw).trim() : "";
  if (!idStr) return;
  keys.add(`ccn:${_normSidebarDedupe(idStr)}`);
  const bare = bareNpiFromCcn(idStr);
  if (bare) keys.add(`npi:${bare}`);
  const medicare = _usMedicareCcnFromIdField(idStr);
  if (medicare) keys.add(`ccn:${medicare}`);
}

/**
 * Build a key set from active (associated) facilities so UserPreferredFacility rows
 * that represent the same site (NPI in ccn vs npi field, hyphenated ids, etc.)
 * can be hidden from Pending Verification.
 */
export function collectActiveFacilitySidebarDedupeKeys(
  facilities: FacilityHierarchyInput[],
): Set<string> {
  const keys = new Set<string>();
  const walk = (f: FacilityHierarchyInput) => {
    const fid = f.facility_id ?? f.id;
    if (fid != null && Number(fid) > 0) {
      keys.add(`id:${fid}`);
    }

    _addSidebarRegistryKeys(keys, f.ccn_number);
    _addSidebarRegistryKeys(keys, f.npi_number);
    _addSidebarRegistryKeys(keys, f.medicare_ccn);
    _addSidebarRegistryKeys(keys, f.facility_unique_id);
    const targeting = f.profile_targeting_json;
    if (targeting && typeof targeting === "object") {
      for (const key of [
        "npi_number",
        "npi",
        "medicare_ccn",
        "ccn_number",
        "ccn",
        "facility_unique_id",
      ]) {
        _addSidebarRegistryKeys(keys, targeting[key]);
      }
    }

    const name = _normSidebarDedupe(
      f.facility_name ?? f.org_name ?? f.organization_name,
    );
    const city = _normSidebarDedupe(f.city);
    const stateName = _normSidebarDedupe(f.state);
    if (name || city || stateName) {
      keys.add(`profile:${name}|${city}|${stateName}`);
    }

    (f.child_facilities || []).forEach(walk);
  };
  facilities.forEach(walk);
  return keys;
}

export type FacilityPlanSidebarInput = FacilityHierarchyInput & {
  is_plan_active?: boolean | null;
  plan_status?: string | null;
  transaction_status?: string | null;
  is_trial?: boolean | number | string | null;
  is_trail?: boolean | number | string | null;
};

/**
 * Sidebar "Active Facilities" — purchased/paid/trial proof only.
 * Verified-before-pay associations often arrive with null plan fields; those must
 * NOT count as Active (they belong under Pending until checkout).
 * Bad / unpaid transaction always wins over a stale MCM is_plan_active=true.
 */
function _sidebarOnTrial(f: FacilityPlanSidebarInput): boolean {
  const planStatus = _normSidebarDedupe(f.plan_status);
  if (planStatus === "trial" || planStatus === "trialing") return true;
  const trial = f.is_trial ?? f.is_trail;
  return trial === true || trial === 1 || trial === "true";
}

export function isPaidActiveFacilityForSidebar(f: FacilityPlanSidebarInput): boolean {
  if ((f as { is_network?: boolean }).is_network) {
    return true;
  }
  const isPlanActive = f.is_plan_active;
  const tx = _normSidebarDedupe(f.transaction_status);
  const planStatus = _normSidebarDedupe(f.plan_status);
  const okTx = new Set(["ok", "success", "completed", "paid", "succeeded"]);
  const badTx = new Set([
    "failed",
    "pending",
    "unpaid",
    "processing",
    "canceled",
    "cancelled",
  ]);
  if (planStatus === "expired") {
    return false;
  }
  if (tx === "failed" || tx === "canceled" || tx === "cancelled") {
    return false;
  }
  if (_sidebarOnTrial(f)) {
    return true;
  }
  if (tx && badTx.has(tx)) {
    return false;
  }
  if (isPlanActive === false) {
    return false;
  }
  if (tx && okTx.has(tx)) {
    return true;
  }
  if (isPlanActive === true) {
    return true;
  }
  return false;
}

/** Identity keys for preferred rows (same shape as {@link collectActiveFacilitySidebarDedupeKeys}). */
export function collectPreferredFacilitySidebarDedupeKeys(
  preferredList: UserPreferredFacilityDedupeInput[],
): Set<string> {
  const keys = new Set<string>();
  for (const preferred of preferredList) {
    const d = _preferredDetailsAsRecord(preferred.facility_details);
    for (const raw of [
      preferred.ccn_number,
      preferred.facility_unique_id,
      d.ccn_number,
      d.npi_number,
      d.npi,
      d.medicare_ccn,
      d.facility_unique_id,
    ]) {
      _addSidebarRegistryKeys(keys, raw);
    }
    const targeting = d.profile_targeting_json;
    if (targeting && typeof targeting === "object" && !Array.isArray(targeting)) {
      const profile = targeting as Record<string, unknown>;
      for (const key of [
        "npi_number",
        "npi",
        "medicare_ccn",
        "ccn_number",
        "ccn",
        "facility_unique_id",
      ]) {
        _addSidebarRegistryKeys(keys, profile[key]);
      }
    }
    const rawFid = d.facility_id;
    if (rawFid != null && rawFid !== "") {
      const n = Number(rawFid);
      if (!Number.isNaN(n) && n > 0) {
        keys.add(`id:${n}`);
      }
    }
    const name = _normSidebarDedupe(
      preferred.facility_name ??
        d.facility_name ??
        d.organization_name ??
        d.org_name,
    );
    const city = _normSidebarDedupe(preferred.city ?? d.city);
    const stateName = _normSidebarDedupe(preferred.state ?? d.state);
    if (name || city || stateName) {
      keys.add(`profile:${name}|${city}|${stateName}`);
    }
  }
  return keys;
}

/** True when facility shares an identity key with the preferred key set. */
export function facilityMatchesSidebarDedupeKeys(
  keys: Set<string>,
  facility: FacilityHierarchyInput,
): boolean {
  if (keys.size === 0) return false;
  for (const k of collectActiveFacilitySidebarDedupeKeys([facility])) {
    if (keys.has(k)) return true;
  }
  return false;
}

export type UserPreferredFacilityDedupeInput = {
  facility_name?: string | null;
  ccn_number?: string | null;
  facility_unique_id?: string | null;
  city?: string | null;
  state?: string | null;
  /** API JSON blob; typed as unknown so strict hub interfaces (e.g. UserPreferredFacilityDetails) assign without an index signature. */
  facility_details?: unknown;
};

function _preferredDetailsAsRecord(details: unknown): Record<string, unknown> {
  if (details && typeof details === "object" && !Array.isArray(details)) {
    return details as Record<string, unknown>;
  }
  return {};
}

/**
 * Returns true when a preferred-facility row matches an active facility in {@link collectActiveFacilitySidebarDedupeKeys}.
 */
export function userPreferredFacilityMatchesActiveDedupeKeys(
  activeKeys: Set<string>,
  preferred: UserPreferredFacilityDedupeInput,
): boolean {
  if (activeKeys.size === 0) return false;
  for (const key of collectPreferredFacilitySidebarDedupeKeys([preferred])) {
    if (activeKeys.has(key)) return true;
  }
  return false;
}

/**
 * Street line for disambiguation: "1700 S TAMIAMI TRL" or "1515 S OSPREY AVE, SUITE 1-A".
 * Prefers structured address fields; otherwise strips city from full_address when possible.
 */
export function extractFacilityStreetLine(f: FacilityDropdownLabelInput): string {
  const fromFields = [f.address_1, f.address_2]
    .map((s) => (typeof s === "string" ? s.trim() : ""))
    .filter(Boolean)
    .join(", ")
    .trim();
  if (fromFields) return fromFields;

  const full =
    typeof f.full_address === "string" ? f.full_address.trim() : "";
  if (!full) return "";

  const city = (f.city || "").trim();
  if (city.length > 0) {
    const lowerFull = full.toLowerCase();
    const needle = `, ${city.toLowerCase()},`;
    const idx = lowerFull.lastIndexOf(needle);
    if (idx > 0) {
      return full.slice(0, idx).trim();
    }
  }

  const first = full.split(",")[0]?.trim() ?? "";
  if (first && /\d/.test(first)) return first;
  return "";
}

/**
 * User-visible name for the numeric/registry id suffix in dropdowns.
 * US uses CMS NPI; other countries use neutral or locally meaningful labels.
 */
export function getFacilityRegistryIdLabel(
  countryCode: string | null | undefined,
): string {
  const c = String(countryCode ?? "")
    .trim()
    .toUpperCase();
  if (c === "CA" || c === "CAN") return "Registry ID";
  if (c === "IN" || c === "IND") return "HFR ID";
  if (c === "AE" || c === "UAE") return "Health facility ID";
  if (c === "US" || c === "USA" || c === "") return "NPI";
  return "Registry ID";
}

function _isUsCountry(countryCode: string | null | undefined): boolean {
  const cc = String(countryCode ?? "").trim().toUpperCase();
  return cc === "" || cc === "US" || cc === "USA";
}

/**
 * Registry digits for picker labels: US requires a valid NPI check digit; other countries
 * keep a strict 10-digit numeric token from {@link bareNpiFromCcn} only.
 */
function _registryIdDigitsForPickerLabel(
  countryCode: string | null | undefined,
  idRaw: string,
): string {
  if (_isUsCountry(countryCode)) {
    return verifiedUsNpiDigitsFromIdField(idRaw);
  }
  const bare = bareNpiFromCcn(idRaw || undefined);
  return bare && /^\d{10}$/.test(bare) ? bare : "";
}

function _pushDropdownIdCandidate(
  seen: Set<string>,
  out: string[],
  raw: string,
): void {
  if (!raw || seen.has(raw)) return;
  seen.add(raw);
  out.push(raw);
}

function _dropdownIdCandidates(f: FacilityDropdownLabelInput): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of [
    _trimStr(f.npi_number),
    _trimStr(f.medicare_ccn),
    _trimStr(f.ccn_number),
    _trimStr(f.facility_unique_id),
  ]) {
    _pushDropdownIdCandidate(seen, out, raw);
  }
  const pt = f.profile_targeting_json;
  if (pt && typeof pt === "object") {
    for (const key of ["npi_number", "npi", "medicare_ccn", "ccn_number", "ccn", "facility_unique_id"]) {
      _pushDropdownIdCandidate(seen, out, _trimStr(pt[key]));
    }
  }
  return out;
}

/** Fill missing {@link npi_number} from session when CCN-only row matches signup selection. */
export function enrichFacilityDropdownRegistryIds<
  T extends FacilityDropdownLabelInput & { facility_name?: string | null },
>(row: T): T {
  const ids = resolveFacilityRegistryDisplayIds(row);
  if (ids.npi && ids.ccn) return row;
  if (ids.npi || !ids.ccn) return row;
  if (typeof window === "undefined") return row;
  try {
    const sessionNpi = sessionStorage.getItem("sessionNpiNumber")?.trim() || "";
    if (!sessionNpi || !isValidUsNpiChecksum(sessionNpi)) return row;
    const sessionName = formatFacilityDisplayName(
      sessionStorage.getItem("sessionFacilityName"),
    );
    const rowName = formatFacilityDisplayName(row.facility_name);
    if (!sessionName || !rowName || sessionName !== rowName) return row;
    return { ...row, npi_number: sessionNpi };
  } catch {
    return row;
  }
}

/** CMS CCN digits from a profile field value (`503820` or `{value: "503820"}`). */
export function medicareCcnFromProfileValue(raw: unknown): string {
  let text = "";
  if (raw && typeof raw === "object" && "value" in raw) {
    text = String((raw as { value: unknown }).value ?? "");
  } else {
    text = String(raw ?? "");
  }
  const t = text.trim();
  return /^\d{5,9}$/.test(t) ? t : "";
}

/** CMS CCN from unique id (`500124`, `5000029`, `user_500124`). Never treats 10-digit NPI candidates as CCN. */
function _usMedicareCcnFromIdField(raw: string): string {
  const t = raw.trim();
  if (!t || /^FAC-/i.test(t)) return "";
  if (/^\d{5,9}$/.test(t)) return t;
  const prefixed = t.match(/_(\d{5,9})$/);
  return prefixed ? prefixed[1] : "";
}

export type FacilityRegistryDisplayIds = {
  /** Verified US NPI (or non-US 10-digit registry id); empty when none. */
  npi: string;
  /** Bare Medicare CCN digits; empty when id field is NPI-shaped or missing. */
  ccn: string;
};

/**
 * Resolve display NPI + CCN from stored id fields.
 * Strips Affable/user prefixes (`prashantpawar_1497803829` → NPI `1497803829`).
 * Does not treat NPI-shaped unique ids as CCN.
 */
export function resolveFacilityRegistryDisplayIds(
  f: FacilityDropdownLabelInput,
): FacilityRegistryDisplayIds {
  let npi = "";
  let ccn = "";
  for (const raw of _dropdownIdCandidates(f)) {
    if (!npi) {
      const digits = _registryIdDigitsForPickerLabel(f.country_code, raw);
      if (digits) npi = digits;
    }
    if (!ccn) {
      const medicare = _usMedicareCcnFromIdField(raw);
      if (medicare) ccn = medicare;
    }
    if (npi && ccn) break;
  }
  return { npi, ccn };
}

/** Stable key for collapsing NPI-only + CCN-only twins of the same site in pickers. */
export function sameSiteFacilityMergeKey(
  f: FacilityHierarchyInput,
): string | null {
  const name = _normSidebarDedupe(
    f.facility_name ?? f.org_name ?? f.organization_name,
  );
  const city = _normSidebarDedupe(f.city);
  const state = _normSidebarDedupe(f.state);
  if (!name && !city && !state) return null;
  const typeKey =
    f.facility_type_id != null
      ? String(f.facility_type_id)
      : _normSidebarDedupe(f.facility_type_slug ?? f.facility_type);
  return `${name}|${city}|${state}|${typeKey}`;
}

/**
 * Collapse duplicate my-facilities rows that share a site but were minted once
 * with NPI and once with CCN. Keeps the NPI-keyed row id; merges both ids for
 * dropdown subtitle (`CCN … NPI …`).
 */
export function mergeSameSiteFacilitiesForDropdown<
  T extends FacilityHierarchyInput,
>(rows: T[]): T[] {
  const singletons: T[] = [];
  const groups = new Map<string, T[]>();

  for (const row of rows) {
    const key = sameSiteFacilityMergeKey(row);
    if (!key || key === "|||") {
      singletons.push(row);
      continue;
    }
    const group = groups.get(key) ?? [];
    group.push(row);
    groups.set(key, group);
  }

  const merged: T[] = [...singletons];

  for (const group of groups.values()) {
    if (group.length === 1) {
      merged.push(group[0]);
      continue;
    }

    const ranked = [...group].sort((a, b) => {
      const aNpi = resolveFacilityRegistryDisplayIds(a).npi ? 1 : 0;
      const bNpi = resolveFacilityRegistryDisplayIds(b).npi ? 1 : 0;
      if (bNpi !== aNpi) return bNpi - aNpi;
      const aId = getFacilityHierarchyId(a) ?? Number.MAX_SAFE_INTEGER;
      const bId = getFacilityHierarchyId(b) ?? Number.MAX_SAFE_INTEGER;
      return aId - bId;
    });

    const canonical = ranked[0];
    let mergedNpi = "";
    let mergedCcn = "";
    for (const row of group) {
      const ids = resolveFacilityRegistryDisplayIds(row);
      if (ids.npi) mergedNpi = ids.npi;
      if (ids.ccn) mergedCcn = ids.ccn;
    }

    const profile =
      canonical.profile_targeting_json &&
      typeof canonical.profile_targeting_json === "object"
        ? { ...canonical.profile_targeting_json }
        : ({} as Record<string, unknown>);
    if (mergedNpi) profile.npi_number = mergedNpi;
    if (mergedCcn) profile.ccn_number = mergedCcn;

    merged.push({
      ...canonical,
      npi_number: mergedNpi || canonical.npi_number,
      profile_targeting_json: Object.keys(profile).length
        ? profile
        : canonical.profile_targeting_json,
    });
  }

  return merged;
}

export type LookupFacilityAssociationIds = {
  ccn_number?: string | null;
  ccn?: string | null;
  npi_number?: string | null;
  npi?: string | null;
  facility_unique_id?: string | null;
};

/**
 * Rules check-user-facility-association / create-from-ccn accept:
 * - exactly 10 digits, or
 * - username_identifier (`^[a-zA-Z0-9_]+_\d+$`, e.g. user_133990 / testuser_ci_5ae_1164876322).
 * Bare 6-digit Medicare CCNs (500124, 133990) are NOT accepted (422).
 */
export function isRulesSafeFacilityIdentifier(
  raw: string | null | undefined,
): boolean {
  const t = String(raw ?? "").trim();
  if (!t) return false;
  if (/^\d{10}$/.test(t)) return true;
  return /^[a-zA-Z0-9_]+_\d+$/.test(t);
}

/**
 * Unique id is always the US NPI when one is present (bare 10 digits).
 * CMS CCN is not the unique id — even if stored as username_503820.
 * Caller prefixes Affable usernames. Bare short CCNs still fall back to NPI.
 */
export function associationUniqueIdFromLookupFacility(
  facility: LookupFacilityAssociationIds | null | undefined,
  fallbackNpi?: string | null,
): string {
  if (!facility && !fallbackNpi) return "";
  const f = facility || {};
  for (const raw of [f.npi_number, f.npi, fallbackNpi, f.ccn_number, f.ccn, f.facility_unique_id]) {
    const digits = verifiedUsNpiDigitsFromIdField(String(raw ?? "").trim());
    if (digits) return digits;
  }
  for (const raw of [f.ccn_number, f.ccn, f.facility_unique_id, fallbackNpi]) {
    const s = String(raw ?? "").trim();
    if (s && isRulesSafeFacilityIdentifier(s) && !/_\d{5,9}$/.test(s)) return s;
  }
  const fallback = String(fallbackNpi ?? "").trim();
  if (fallback && isRulesSafeFacilityIdentifier(fallback)) return fallback;
  return fallback;
}

export type FacilityDropdownRegistryBadge = {
  label: string;
  value: string;
};

/**
 * NPI when any id field has a valid 10-digit NPI (including `user_1245319599`).
 * Else US Medicare CCN when the stored id is 6–10 digits and not NPI-shaped
 * (including `user_500124`).
 *
 * Prefer {@link getFacilityDropdownRegistryPhrase} when both CCN and NPI should show.
 */
export function getFacilityDropdownRegistryBadge(
  f: FacilityDropdownLabelInput,
): FacilityDropdownRegistryBadge | null {
  const { npi, ccn } = resolveFacilityRegistryDisplayIds(f);
  if (npi) {
    return { label: getFacilityRegistryIdLabel(f.country_code), value: npi };
  }
  if (ccn && _isUsCountry(f.country_code)) {
    return { label: "CCN", value: ccn };
  }
  return null;
}

/**
 * Picker subtitle / paren suffix (US Medicare):
 * Both: `CCN 123456 NPI 1234567890`
 * CCN only: `CCN 123456`
 * NPI only: `NPI 1234567890`
 * Non-US registry ids keep their labeled form (Registry ID / HFR ID / …).
 */
export function getFacilityDropdownRegistryPhrase(
  f: FacilityDropdownLabelInput,
): string {
  const { npi, ccn } = resolveFacilityRegistryDisplayIds(f);
  const us = _isUsCountry(f.country_code);
  const showCcn = Boolean(ccn && us);
  if (showCcn && npi) return `CCN ${ccn} NPI ${npi}`;
  if (showCcn) return `CCN ${ccn}`;
  if (!npi) return "";
  if (us) return `NPI ${npi}`;
  return `${getFacilityRegistryIdLabel(f.country_code)} ${npi}`;
}

function _registryParenSuffix(f: FacilityDropdownLabelInput): string {
  const phrase = getFacilityDropdownRegistryPhrase(f);
  return phrase ? ` (${phrase})` : "";
}

/**
 * Picker label: "Name – street, City, ST" when a street line exists; otherwise
 * "Name – City, ST (NPI …)" when a verified US NPI or non-US registry id is present, so
 * duplicate legal names remain distinguishable even if address was never stored.
 */
export function formatFacilityDropdownLabel(
  f: FacilityDropdownLabelInput
): string {
  f = enrichFacilityDropdownRegistryIds(f);
  const name = formatFacilityDisplayName(f.facility_name);
  const city = (f.city || "").trim();
  const state = (f.state || "").trim();
  const locality = [city, state].filter(Boolean).join(", ");
  const street = extractFacilityStreetLine(f);
  const suffix = _registryParenSuffix(f);

  if (street) {
    if (locality) return `${name} – ${street}, ${locality}${suffix}`;
    return `${name} – ${street}${suffix}`;
  }

  const base = locality ? `${name} – ${locality}` : name;
  return `${base}${suffix}`;
}

/** User-visible facility name, normalized to uppercase for consistent display. */
export function formatFacilityDisplayName(name: string | null | undefined): string {
  return String(name ?? "").trim().toUpperCase();
}

/**
 * First line for facility pickers: legal / display name only (no address).
 */
export function getFacilityDropdownNameLine(f: FacilityHierarchyInput): string {
  const name = (
    f.facility_name ||
    f.org_name ||
    f.organization_name ||
    ""
  ).trim();
  if (name) return formatFacilityDisplayName(name);
  const id = getFacilityHierarchyId(f);
  return id != null ? `Facility #${id}` : "";
}

/**
 * Second line for facility pickers: street + locality, else locality + registry id,
 * else registry id, else full_address — aligned with {@link formatFacilityDropdownLabel}
 * but without the name prefix.
 */
export function getFacilityDropdownSubtitleLine(
  f: FacilityHierarchyInput,
): string {
  f = enrichFacilityDropdownRegistryIds(f);
  if (f.is_network && (f.facility_type || "").trim()) {
    return (f.facility_type || "").trim();
  }
  const city = (f.city || "").trim();
  const state = (f.state || "").trim();
  const locality = [city, state].filter(Boolean).join(", ");
  const street = extractFacilityStreetLine(f);
  const phrase = getFacilityDropdownRegistryPhrase(f);

  if (street) {
    const loc = locality ? `${street}, ${locality}` : street;
    return phrase ? `${loc} (${phrase})` : loc;
  }
  if (locality) {
    return phrase ? `${locality} (${phrase})` : locality;
  }
  if (phrase) return phrase;
  const full =
    typeof f.full_address === "string" ? f.full_address.trim() : "";
  if (full) return full;
  return "—";
}

/** Optional fields used to explain how a facility type was inferred (NPI / taxonomy / CCN). */
export type FacilityTypeMetaInput = FacilityDropdownLabelInput & {
  primary_taxonomy_code?: string | null;
  primary_taxonomy_desc?: string | null;
  profile_targeting_json?: Record<string, unknown> | null;
};

function _readTaxonomyFromProfile(
  profile: Record<string, unknown> | null | undefined,
): { code: string; desc: string } {
  if (!profile) return { code: "", desc: "" };
  const directCode = _trimStr(profile.primary_taxonomy_code);
  const directDesc = _trimStr(profile.primary_taxonomy_desc);
  if (directCode || directDesc) return { code: directCode, desc: directDesc };
  const pt = profile.primary_taxonomy;
  if (pt && typeof pt === "object" && !Array.isArray(pt)) {
    const o = pt as Record<string, unknown>;
    return {
      code: _trimStr(o.code),
      desc: _trimStr(o.description) || _trimStr(o.desc),
    };
  }
  return { code: _trimStr(profile.taxonomy_code), desc: "" };
}

/**
 * Secondary line under facility type: NUCC provider taxonomy code when known.
 * NPI stays on the facility selector only. Returns "-" when no valid NUCC code.
 */
export function getFacilityTypeSupplementLine(f: FacilityTypeMetaInput): string {
  const row = f as Record<string, unknown>;
  const fromProfile = _readTaxonomyFromProfile(f.profile_targeting_json ?? null);
  const nestedPt = row.primary_taxonomy;
  let fromNestedPt = "";
  if (nestedPt && typeof nestedPt === "object" && !Array.isArray(nestedPt)) {
    fromNestedPt = _trimStr(
      (nestedPt as Record<string, unknown>).code as string | undefined,
    );
  }
  const tax =
    _trimStr(f.primary_taxonomy_code) ||
    _trimStr(row.primary_taxonomy_code) ||
    fromProfile.code ||
    fromNestedPt ||
    _trimStr(row.taxonomy_code as string | undefined);

  if (tax && _isPlausibleNuccTaxonomyCode(tax)) {
    return tax.trim();
  }
  return "-";
}

function _parseNestedObjectMaybe(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw) as unknown;
      if (typeof p === "object" && p && !Array.isArray(p)) {
        return p as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Merge root facility row + nested `facility_details` so taxonomy and type fields
 * resolve consistently for {@link getFacilityTypeSupplementLine}.
 */
export function mergeFacilityDetailsForTaxonomy(
  facility: Record<string, unknown> | null | undefined,
): FacilityTypeMetaInput & Record<string, unknown> {
  if (!facility || typeof facility !== "object") {
    return {} as FacilityTypeMetaInput & Record<string, unknown>;
  }
  const row: Record<string, unknown> = { ...facility };
  const details = _parseNestedObjectMaybe(row.facility_details);
  if (details) {
    if (!row.primary_taxonomy_code && details.primary_taxonomy_code != null) {
      row.primary_taxonomy_code = details.primary_taxonomy_code as string;
    }
    if (!row.primary_taxonomy_desc && details.primary_taxonomy_desc != null) {
      row.primary_taxonomy_desc = details.primary_taxonomy_desc as string;
    }
    if (!row.profile_targeting_json && details.profile_targeting_json) {
      row.profile_targeting_json =
        details.profile_targeting_json as FacilityTypeMetaInput["profile_targeting_json"];
    }
    if (!row.taxonomy_code && details.taxonomy_code != null) {
      row.taxonomy_code = details.taxonomy_code;
    }
    if (!row.primary_taxonomy && details.primary_taxonomy != null) {
      row.primary_taxonomy = details.primary_taxonomy;
    }
    if (!row.facility_type && details.facility_type != null) {
      row.facility_type = details.facility_type;
    }
    if (!row.facility_type_slug && details.facility_type_slug != null) {
      row.facility_type_slug = details.facility_type_slug;
    }
    if (!row.vertical && details.vertical != null) {
      row.vertical = details.vertical;
    }
  }
  return row as FacilityTypeMetaInput & Record<string, unknown>;
}

/**
 * Healthcare primary line under "Facility Type": display name or slug fallback.
 * For aviation, use {@link getAviationProfileLabel} instead.
 */
export function getHealthcareFacilityTypeTitle(
  f: FacilityTypeMetaInput | Record<string, unknown> | null | undefined,
): string {
  if (!f || typeof f !== "object") return "—";
  const row = f as Record<string, unknown>;
  const rhc = resolveRhcFacilityTypeOverride(row);
  if (rhc) return rhc.display_name;
  const name = _trimStr(row.facility_type as string);
  if (name) return name;
  const slug = _trimStr(row.facility_type_slug as string);
  if (slug) return slug.replace(/_/g, " ");
  return "—";
}

export function getFacilityHierarchyId(
  facility: FacilityHierarchyInput
): number | null {
  return facility.facility_id ?? facility.id ?? null;
}

function pickHierarchyField<T>(
  apiValue: T | null | undefined,
  sessionValue: T | null | undefined,
): T | null | undefined {
  if (apiValue === null) return null;
  if (apiValue !== undefined && apiValue !== null && apiValue !== "") return apiValue;
  return sessionValue ?? apiValue;
}

/** Merge parent/child group fields from session when layout cache is stale after declare. */
export function mergeSessionHierarchyFields<T extends FacilityHierarchyInput>(
  facility: T,
): T {
  const stored = getSelectedFacilityFromSession<FacilityHierarchyInput>();
  if (!stored) return facility;
  const id = getFacilityHierarchyId(facility);
  const storedId = getFacilityHierarchyId(stored);
  if (id == null || storedId !== id) return facility;
  return {
    ...facility,
    facility_group_id: pickHierarchyField(
      facility.facility_group_id,
      stored.facility_group_id,
    ),
    parent_facility_group_id: pickHierarchyField(
      facility.parent_facility_group_id,
      stored.parent_facility_group_id,
    ),
    is_parent_facility: pickHierarchyField(
      facility.is_parent_facility,
      stored.is_parent_facility,
    ),
    is_child_facility: pickHierarchyField(
      facility.is_child_facility,
      stored.is_child_facility,
    ),
  };
}

export function isParentFacility(facility: FacilityHierarchyInput): boolean {
  return isParentFacilityWithChildren(facility);
}

/** True when the dropdown facility is a parent with at least one child (Network tab). */
export function isParentFacilityWithChildren(facility: FacilityHierarchyInput | null | undefined): boolean {
  if (!facility) return false;
  const children = facility.child_facilities;
  return Array.isArray(children) && children.length > 0;
}

/** Network picker options: linked children are never listed under their parent. */
export function buildTopLevelFacilityOptions<T extends FacilityHierarchyInput>(
  facilities: T[]
): GroupedFacilityOption<T>[] {
  return buildGroupedFacilityOptions(facilities).filter((option) => option.depth === 0);
}

export function buildGroupedFacilityOptions<T extends FacilityHierarchyInput>(
  facilities: T[]
): GroupedFacilityOption<T>[] {
  const childIds = new Set<number>();
  const childrenByParentGroupId = new Map<string, T[]>();

  const addChild = (parentGroupId: string, child: FacilityHierarchyInput) => {
    const childId = getFacilityHierarchyId(child);
    if (childId == null) return;

    const children = childrenByParentGroupId.get(parentGroupId) || [];
    if (!children.some((existing) => getFacilityHierarchyId(existing) === childId)) {
      children.push(child as T);
    }
    childrenByParentGroupId.set(parentGroupId, children);
    childIds.add(childId);
  };

  facilities.forEach((facility) => {
    const parentGroupId = facility.facility_group_id || "";
    if (parentGroupId) {
      (facility.child_facilities || []).forEach((child) =>
        addChild(parentGroupId, child)
      );
    }
  });

  facilities.forEach((facility) => {
    if (facility.parent_facility_group_id) {
      addChild(facility.parent_facility_group_id, facility);
    }
  });

  const options: GroupedFacilityOption<T>[] = [];
  const renderedIds = new Set<number>();

  facilities.forEach((facility) => {
    const facilityId = getFacilityHierarchyId(facility);
    if (facilityId != null && childIds.has(facilityId)) return;

    const childFacilities = facility.facility_group_id
      ? childrenByParentGroupId.get(facility.facility_group_id) || []
      : [];

    if (facilityId != null) renderedIds.add(facilityId);
    options.push({
      facility,
      depth: 0,
      isParent: childFacilities.length > 0,
      key: `facility-${facilityId ?? options.length}`,
    });

    childFacilities.forEach((child) => {
      const childId = getFacilityHierarchyId(child);
      if (childId != null) renderedIds.add(childId);
      options.push({
        facility: child,
        depth: 1,
        isParent: false,
        key: `facility-${facilityId ?? "parent"}-child-${childId ?? options.length}`,
      });
    });
  });

  facilities.forEach((facility) => {
    const facilityId = getFacilityHierarchyId(facility);
    if (facilityId != null && renderedIds.has(facilityId)) return;
    options.push({
      facility,
      depth: 0,
      isParent:
        (facility.facility_group_id
          ? childrenByParentGroupId.get(facility.facility_group_id) || []
          : []
        ).length > 0 || isParentFacilityWithChildren(facility),
      key: `facility-${facilityId ?? options.length}`,
    });
  });

  return options;
}

export function extractFacilityName(
  facility?: Record<string, any> | null
): string {
  if (!facility || typeof facility !== "object") return "";

  const candidate =
    facility?.facility_name ||
    facility?.site_name ||
    facility?.facilityName ||
    facility?.siteName;

  return typeof candidate === "string" && candidate.trim()
    ? candidate.trim()
    : "";
}


